import React, { useState, useContext, useCallback, useEffect, useMemo } from 'react';
import { modeUIContext, showSuccessNotification,
    setIsEditingForm,
    showDialog,
    DialogType,
    DialogResponse,
    DialogResponseCode } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import { Agent, AgentRole, ProjectAgent } from '@moderepo/mode-internal-apis';
import { selectProjectById,
    ritzDataContext,
    selectProjectAgents,
    updateProjectAgentRole,
    fetchProjectAgents,
    selectLoggedInAgent,
    selectProjectAgent,
    createProjectAgent } from '@moderepo/mode-data-state-ritz';
import { capitalizeAll, EMAIL_VALIDATION_REGEX, SelectInputOption } from '@moderepo/mode-app-components';
import { getAgentRoleName } from '@moderepo/mode-app-components-internal';
import produce from 'immer';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import { useRitzTranslation } from '../../utils/customHooks';
import { AgentInfo, AgentInfoProps, AgentInfoUpdatableInputs } from '../../components/agent/AgentInfo';

interface InputErrors {
    email?: string | undefined;
    role?: string | undefined;
}

const findAgent = (agentList: readonly ProjectAgent[] | undefined, agentId: number | undefined) => {
    const result = agentList?.find((agent) => {
        return agent.agentId === agentId;
    });
    return result;
};

export interface AgentInfoContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly agentId?: number;

    readonly newAgentSettings?: { // Only available if this container is used for creating new agent
        readonly onNewAgentCreated: ()=> void;
        readonly onNewAgentCanceled: ()=> void;
    }
}

/**
 * This is the container for the AgentInfoComp. It provides the logic for the AgentInfoComp which is a dumb component that does not
 * do anything other than displaying data. This container will take care of all the logic e.g. edit agent, etc...
 * @param props
 */

export const AgentInfoContainer = (props: AgentInfoContainerProps) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);


    const agents = selectProjectAgents(dataState, props.projectId);

    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, props.projectId, loggedInAgent.id) : undefined;
    const targetAgent = findAgent(agents, props.agentId);
    const isNewAgent = !props.agentId;

    const [inputErrors, setInputErrors] = useState<InputErrors>({});


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);

    /**
     * Automatically set editing mode to true on component init if props.agentId is undefined
     */
    useEffect(() => {
        if (props.agentId === undefined) {
            onChangeEditingMode(true);
        }
    }, [props.agentId, onChangeEditingMode]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        setInputErrors({});
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);


    /**
     * Create Input settings for the AgentInfoComp and cache it so that it doesn't get created on every re-render
     */
    const inputsSettings = useMemo((): AgentInfoProps['fields'] => {

        return {
            name: {
                label       : capitalizeAll(trans('label.user_name')),
                displayValue: targetAgent?.name || '',
                value       : targetAgent?.name,
                hidden      : isNewAgent,
            },
            verified: {
                label       : capitalizeAll(trans('label.status')),
                displayValue: capitalizeAll(trans(targetAgent?.verified ? 'label.verified' : 'label.not_verified')),
                value       : targetAgent?.verified,
                hidden      : isNewAgent,
            },
            id: {
                label       : capitalizeAll(trans('label.id')),
                displayValue: targetAgent?.agentId.toString(),
                value       : targetAgent?.agentId,
                hidden      : isNewAgent,
            },
            email: {
                label       : capitalizeAll(trans('label.user_email')),
                displayValue: targetAgent?.email || capitalizeAll(trans('label.none')),
                editable    : isNewAgent,
                value       : targetAgent?.email,
                error       : inputErrors?.email ? trans(inputErrors.email) : undefined,
            },
            role: {
                label       : capitalizeAll(trans('label.role')),
                displayValue: targetAgent?.role,
                value       : targetAgent?.role,
                editable    : projectAgent?.role === AgentRole.ADMIN,
                options     : Object.values(AgentRole).map((role: AgentRole): SelectInputOption<AgentRole> => {
                    return {
                        label: trans(getAgentRoleName(role)).toUpperCase(),
                        value: role,
                    };
                }),
                error: inputErrors?.role ? trans(inputErrors.role) : undefined,
            },
            creationTime: {
                label       : capitalizeAll(trans('label.date_created')),
                displayValue: targetAgent ? getDateTimeAndElapsedTime(targetAgent.creationTime, trans) : '',
                value       : targetAgent?.creationTime,
                hidden      : isNewAgent,
            },
        };

    }, [trans, targetAgent, isNewAgent, inputErrors.email, inputErrors.role, projectAgent?.role]);


    const onCancelEdit = useCallback(() => {
        closeEditing();
        if (props.newAgentSettings !== undefined) {
            props.newAgentSettings.onNewAgentCanceled();
        }
    }, [closeEditing, props.newAgentSettings]);


    /**
     * Update existing agent
     */
    const onUpdateExistingAgent = useCallback(async (currentSelectedAgent: ProjectAgent, inputs: AgentInfoUpdatableInputs) => {

        try {
        // Agent is updating existing agent
            const newInputErrors: InputErrors = ({});

            if (!inputs.role) {
                newInputErrors.role = 'error.input.generic.empty';
            }

            // Update input errors if there are any
            setInputErrors(newInputErrors);

            if (!newInputErrors.role && inputs.role) {
                // Inputs validated successfully
                if (inputs.role !== currentSelectedAgent.role) {
                    uiDispatch(showDialog({
                        type       : DialogType.CONFIRM,
                        title      : 'dialog.confirm_change_agent_role.title',
                        message    : 'dialog.confirm_change_agent_role.message',
                        messageData: {
                            name: (currentSelectedAgent.name ? currentSelectedAgent.name : `#${currentSelectedAgent.agentId}`),
                            role: capitalizeAll(trans(getAgentRoleName(inputs.role))),
                        },
                        positiveButton: 'label.yes',
                        negativeButton: 'label.cancel',
                        onClose       : async (response?: DialogResponse): Promise<void> => {
                            if (response && response.code === DialogResponseCode.POSITIVE) {
                                dataDispatch(updateProjectAgentRole(props.projectId, currentSelectedAgent.agentId,
                                    inputs.role as string)).then(() => {
                                    uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'label.agent' }));
                                    closeEditing();
                                    dataDispatch(fetchProjectAgents(props.projectId)).catch(() => { /* ignore error */ });
                                }).catch(() => {
                                    /* do nothing */
                                });
                            }
                        },
                    }));
                } else {
                    // agentRole is the same so no need to make api call
                    closeEditing();
                }
            }
        } catch (error) {
            /* ignore error */
        }
    }, [dataDispatch, uiDispatch, closeEditing, props.projectId, trans]);

    /**
     * Update existing agent
     */
    const onCreateAgent = useCallback(async (inputs: AgentInfoUpdatableInputs) => {

        try {
            // reset the errors
            const newInputErrors: InputErrors = ({});

            if (!inputs.role) {
                newInputErrors.role = 'error.input.generic.empty';
            }

            if (!inputs.email) {
                newInputErrors.email = 'error.input.email.empty';
            } else if (!EMAIL_VALIDATION_REGEX.test(inputs.email)) {
                newInputErrors.email = 'error.input.email.invalid';
            }

            // Update input errors if there are any
            setInputErrors(newInputErrors);

            if (inputs.role && inputs.email && !newInputErrors.role && !newInputErrors.email) {
                dataDispatch(createProjectAgent(props.projectId, inputs.email, inputs.role)).then(() => {
                    uiDispatch(showSuccessNotification('notif.agent_added_successfully'));
                    closeEditing();
                    dataDispatch(fetchProjectAgents(props.projectId));
                    if (props.newAgentSettings) {
                        props.newAgentSettings.onNewAgentCreated();
                    }
                }).catch(() => {
                    /* do nothing */
                });
            }
        } catch (error) {
            /* ignore error */
        }
    }, [closeEditing, dataDispatch, props.newAgentSettings, props.projectId, uiDispatch]);


    /**
     * Handler for when the user click Save input from the agent form
     */
    const onAgentInfoFormSubmit = useCallback(async (inputs: AgentInfoUpdatableInputs) => {
        if (targetAgent) {
            await onUpdateExistingAgent(targetAgent, inputs);
        } else {
            await onCreateAgent(inputs);
        }
    }, [targetAgent, onUpdateExistingAgent, onCreateAgent]);

    const componentActions = useMemo(() => {
        // only have agent to change agent roles if they are 'admin'
        if (projectAgent?.role === AgentRole.ADMIN) {
            return {
                startEdit: {
                    onClick: () => {
                        onChangeEditingMode(true);
                    },
                },
                cancelEdit: {
                    label  : capitalizeAll(trans('label.cancel')),
                    onClick: onCancelEdit,
                },
                saveUpdate: {
                    label  : !targetAgent ? capitalizeAll(trans('label.create')) : capitalizeAll(trans('label.save')),
                    onClick: onAgentInfoFormSubmit,
                },
            };
        }

        return undefined;

    }, [onChangeEditingMode, onAgentInfoFormSubmit, targetAgent, trans, onCancelEdit, projectAgent?.role]);

    /**
     * On user form input change, we will clear the error message for that field.
     */
    const onAgentInfoFormInputChange = useCallback((fieldName: string) => {
        setInputErrors(produce(inputErrors, (draft: InputErrors) => {
            draft[fieldName] = undefined;
        }));
    }, [inputErrors]);

    return (
        <div>
            <AgentInfo
                className={clsx(props.className, isEditing && 'editing')}
                icon="headset_mic"
                title={capitalizeAll(trans(props.agentId ? 'label.agent_info' : 'label.add_agent'))}
                fields={inputsSettings}
                isEditing={isEditing}
                actions={componentActions}
                onFieldClicked={() => {
                    onChangeEditingMode(true);
                }}
                onFormInputChange={onAgentInfoFormInputChange}
            />
        </div>
    );
};
